import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { API_URL } from '../utils/constants';
import { getFullUrl } from '../utils/api';

const PlayerContext = createContext();

export const usePlayer = () => {
  const context = useContext(PlayerContext);
  if (!context) {
    throw new Error('usePlayer must be used within a PlayerProvider');
  }
  return context;
};

export const PlayerProvider = ({ children }) => {
  const [playingTrack, setPlayingTrack] = useState(null);
  const [playUrl, setPlayUrl] = useState(null);
  const [wavePlaylist, setWavePlaylist] = useState([]);
  const [currentWaveIndex, setCurrentWaveIndex] = useState(0);
  const [nextTrackUrl, setNextTrackUrl] = useState(null);
  const [beatSpeed, setBeatSpeed] = useState(1.0);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  const handlePlayYandex = useCallback(async (track) => {
    if (playingTrack && playingTrack.id === track.id && playingTrack.source === 'yandex') {
      setPlayingTrack(null);
      setPlayUrl(null);
      return;
    }
    
    console.log('[PLAYER] handlePlayYandex вызван', { trackId: track.id, trackName: track.name, hasStreamUrl: !!track.streamUrl });
    
    setPlayingTrack({ ...track, source: 'yandex' });
    
    // Если у трека уже есть streamUrl из карточки, используем его сразу
    if (track.streamUrl) {
      console.log('[PLAYER] Используем streamUrl из карточки', { streamUrl: track.streamUrl });
      setPlayUrl(track.streamUrl);
      return;
    }
    
    // Если streamUrl нет, формируем его на основе track.id
    if (track.id) {
      const streamUrl = `/api/yandex/stream?track_id=${track.id}`;
      console.log('[PLAYER] Формируем streamUrl из track.id', { streamUrl });
      setPlayUrl(streamUrl);
      return;
    }
    
    // Если нет ни streamUrl, ни id - ошибка
    console.error('[PLAYER] Нет streamUrl и id для трека', { track });
    alert('Ошибка: нет данных для воспроизведения трека');
    setPlayingTrack(null);
    setPlayUrl(null);
  }, [playingTrack]);

  const handlePlayYouTube = useCallback(async (video) => {
    if (!video.url) return;
    
    if (playingTrack && playingTrack.id === video.id && playingTrack.source === 'youtube') {
      setPlayingTrack(null);
      setPlayUrl(null);
      return;
    }
    
    setPlayingTrack({ ...video, source: 'youtube' });
    
    // Используем streamUrl из карточки, если есть
    if (video.streamUrl) {
      setPlayUrl(video.streamUrl);
      return;
    }
    
    // Формируем streamUrl из video.url
    if (video.url) {
      const streamUrl = `/api/youtube/stream?url=${encodeURIComponent(video.url)}`;
      setPlayUrl(streamUrl);
      return;
    }
    
    // Если нет данных - ошибка
    console.error('[PLAYER] Нет streamUrl и url для YouTube видео', { video });
    alert('Ошибка: нет данных для воспроизведения видео');
    setPlayingTrack(null);
    setPlayUrl(null);
  }, [playingTrack]);

  const handlePlaySpotify = useCallback(async (track) => {
    if (!track.id && !track.url) return;
    
    if (playingTrack && playingTrack.id === track.id && playingTrack.source === 'spotify') {
      setPlayingTrack(null);
      setPlayUrl(null);
      return;
    }
    
    setPlayingTrack({ ...track, source: 'spotify' });
    
    // Используем streamUrl из карточки, если есть
    if (track.streamUrl) {
      setPlayUrl(track.streamUrl);
      return;
    }
    
    // Формируем streamUrl из track.id или track.url
    if (track.id) {
      const streamUrl = `/api/spotify/stream?track_id=${encodeURIComponent(track.id)}`;
      setPlayUrl(streamUrl);
      return;
    }
    
    if (track.url) {
      // Извлекаем track_id из Spotify URL, если это URL
      const trackIdMatch = track.url.match(/track\/([a-zA-Z0-9]+)/);
      if (trackIdMatch) {
        const streamUrl = `/api/spotify/stream?track_id=${encodeURIComponent(trackIdMatch[1])}`;
        setPlayUrl(streamUrl);
        return;
      }
      // Если не удалось извлечь track_id, используем URL как есть
      const streamUrl = `/api/spotify/stream?track_id=${encodeURIComponent(track.url)}`;
      setPlayUrl(streamUrl);
      return;
    }
    
    // Если нет данных - ошибка
    console.error('[PLAYER] Нет streamUrl, id и url для Spotify трека', { track });
    alert('Ошибка: нет данных для воспроизведения трека');
    setPlayingTrack(null);
    setPlayUrl(null);
  }, [playingTrack]);

  const handlePlaySoundcloud = useCallback(async (track) => {
    if (!track.id && !track.url) return;

    if (playingTrack && playingTrack.id === track.id && playingTrack.source === 'soundcloud') {
      setPlayingTrack(null);
      setPlayUrl(null);
      return;
    }

    setPlayingTrack({ ...track, source: 'soundcloud' });

    if (track.streamUrl) {
      setPlayUrl(track.streamUrl);
    } else if (track.url) {
      setPlayUrl(`/api/soundcloud/stream?url=${encodeURIComponent(track.url)}`);
    } else {
      setPlayUrl(`/api/soundcloud/stream?track_id=${encodeURIComponent(track.id)}`);
    }
  }, [playingTrack]);

  const handleStop = useCallback(() => {
    setPlayingTrack(null);
    setPlayUrl(null);
    setWavePlaylist([]);
    setCurrentWaveIndex(0);
    setNextTrackUrl(null);
  }, []);

  const playTrackFromPlaylist = useCallback(async (track, index) => {
    try {
      setPlayingTrack(track);
      setCurrentWaveIndex(index);
      
      // Используем streamUrl из трека, если он есть
      if (track.streamUrl) {
        setPlayUrl(track.streamUrl);
      } else if (track.id) {
        // Формируем streamUrl из track.id
        const streamUrl = `/api/yandex/stream?track_id=${track.id}`;
        setPlayUrl(streamUrl);
      } else {
        throw new Error('Нет данных для воспроизведения трека');
      }
      
      // Предзагружаем следующий трек если есть
      if (index < wavePlaylist.length - 1) {
        const nextTrack = wavePlaylist[index + 1];
        // Используем streamUrl из следующего трека, если он есть
        if (nextTrack.streamUrl) {
          setNextTrackUrl(nextTrack.streamUrl);
        } else if (nextTrack.id) {
          const nextStreamUrl = `/api/yandex/stream?track_id=${nextTrack.id}`;
          setNextTrackUrl(nextStreamUrl);
        } else {
          setNextTrackUrl(null);
        }
      } else {
        setNextTrackUrl(null);
      }
    } catch (error) {
      console.error('Ошибка проигрывания трека:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Неизвестная ошибка';
      alert(`Ошибка при воспроизведении трека: ${errorMessage}`);
    }
  }, [wavePlaylist]);

  const playNextTrack = useCallback(() => {
    if (currentWaveIndex < wavePlaylist.length - 1) {
      const nextIndex = currentWaveIndex + 1;
      playTrackFromPlaylist(wavePlaylist[nextIndex], nextIndex);
    }
  }, [currentWaveIndex, wavePlaylist, playTrackFromPlaylist]);

  const playPrevTrack = useCallback(() => {
    if (currentWaveIndex > 0) {
      const prevIndex = currentWaveIndex - 1;
      playTrackFromPlaylist(wavePlaylist[prevIndex], prevIndex);
    }
  }, [currentWaveIndex, wavePlaylist, playTrackFromPlaylist]);

  const value = {
    playingTrack,
    playUrl,
    wavePlaylist,
    setWavePlaylist,
    currentWaveIndex,
    nextTrackUrl,
    beatSpeed,
    setBeatSpeed,
    isPlaying,
    setIsPlaying,
    handlePlayYandex,
    handlePlayYouTube,
    handlePlaySpotify,
    handlePlaySoundcloud,
    handleStop,
    playTrackFromPlaylist,
    playNextTrack,
    playPrevTrack,
    audioRef
  };

  return (
    <PlayerContext.Provider value={value}>
      {children}
    </PlayerContext.Provider>
  );
};

